import { graphql } from 'gatsby';
import React from 'react';
import Seo from '../../components/SEO';
import { motion } from '../../components/UI/framer-motion-custom';
import Cover, { CoverHeading, coverThemes } from '../../components/Cover/Cover';
import HeaderTheme from '../../components/Header/HeaderTheme';
import LocationCard from '../../components/LocationCard/LocationCard';
import LocationDetails from '../../components/LocationDetails/LocationDetails';
import Map, { Marker } from '../../components/Map/Map';
import Promotions from '../../components/Promotions/Promotions';
import RecentPosts from '../../components/RecentPosts/RecentPosts';
import Services from '../../components/Services/Services';
import SplitBody from '../../components/SplitBody/SplitBody';
import BackLink from '../../components/UI/BackLink/BackLink';
import Button, { sizes } from '../../components/UI/Button/Button';
import {
  CustomAccordion,
  CustomAccordionItem,
  CustomAccordionItemHeading,
  CustomAccordionItemButton,
  CustomAccordionItemPanel,
} from '../../components/UI/CustomAccordion/CustomAccordion';
import { Gallery } from '../../components/UI/Gallery/Gallery';
import { GridReveal, GridItem } from '../../components/UI/Grid/Grid';
import HtmlContent from '../../components/UI/HtmlContent/HtmlContent';
import Navbar from '../../components/UI/Navbar/Navbar';
import Section from '../../components/UI/Section/Section';
import Teaser from '../../components/UI/Teaser/Teaser';
import Type from '../../components/UI/Type/Type';
import usePromotions from '../../hooks/usePromotions';
import * as utilities from '../../styles/utilities.module.scss';

const LocationMap = ({ title, details }) => {
  return (
    !details.mapLocation ? null : (
      <Map zoom={details.mapLocation.zoom}>
        <Marker
          coords={[
            details.mapLocation.longitude,
            details.mapLocation.latitude,
          ]}
          popup={
            <LocationCard
              isCard
              title={title}
              address={details.address}
              telephone={details.telephone}
              image={details.gallery[ 0 ]}
            />
          }
        />
      </Map>
    )
  )
};

const LocationTemplate = ({
  data: {
    page: { id, title, sectionPromotions, locationDetails, metadata, seo },
  },
  location: { pathname }
}) => {
  const navigation = [
    { label: 'Gallery', path: '#gallery' },
    { label: 'Overview', path: '#overview' },
    { label: 'Services', path: '#services' },
    ...(!!locationDetails.directions?.length
      ? [ { label: 'Getting here', path: '#getting-here' } ]
      : []),
    { label: 'Consultants', path: `/our-experts/?location=${id}` },
  ];
  const promotions = usePromotions(sectionPromotions.promotions, locationDetails.services, {
    excludePath: pathname
  });

  return (
    <>
      <HeaderTheme />
      
      <Seo 
        title={`Location: ${seo.title}`} 
        description={metadata.metaDescription} 
        image={
          metadata.metaImage
            || (locationDetails.gallery?.length ? locationDetails.gallery[ 0 ] : null)
        }
        seo={seo}
      />

      <Cover
        theme={coverThemes.TAN}
        hasSpacer
      >
        <Teaser>Locations</Teaser>
        <CoverHeading>{`**${title}**`}</CoverHeading>
        {locationDetails.medicalImagingPartnership && 
          <motion.p className={utilities.mb0}>
            Part of <a href="https://medicalimaging.org.uk/" target="_blank" rel="noreferrer">Medical Imaging Partnership</a>
          </motion.p>
        }
       
      </Cover>

      <Navbar nodes={navigation} pageContext={{ location: title }} />

      <Gallery images={locationDetails.gallery} id="gallery" isHero />

      <SplitBody aside={<LocationMap title={title} details={locationDetails}/>} id="overview">
        <Type.H2>**Overview**</Type.H2>
        <LocationDetails {...locationDetails} directionsLink="getting-here" />
      </SplitBody>

      <Promotions items={promotions} />

      <Services
        heading="Available **services**"
        headingProps={{ isCentered: true }}
        nodes={locationDetails.services}
        isInnerOffsetY={false}
      />

      {!!locationDetails.directions?.length && (
        <Section
          heading="Getting **here**"
          headingProps={{ isCentered: true }}
          id="getting-here"
        >
          <GridReveal justify="center">
            <GridItem lg="8" className={utilities.textCenter}>
              <CustomAccordion collapsible isReverse>
                {locationDetails.directions?.map(
                  ({ title, instructions }, index) => (
                    <CustomAccordionItem uuid={index} key={index}>
                      <CustomAccordionItemHeading>
                        <CustomAccordionItemButton>
                          {title}
                        </CustomAccordionItemButton>
                      </CustomAccordionItemHeading>
                      <CustomAccordionItemPanel>
                        <HtmlContent content={instructions} />
                      </CustomAccordionItemPanel>
                    </CustomAccordionItem>
                  )
                )}
              </CustomAccordion>

              {locationDetails.googleDirections && (
                <Button
                  to={locationDetails.googleDirections}
                  size={sizes.LARGE}
                >
                  Get directions
                </Button>
              )}
            </GridItem>
          </GridReveal>
        </Section>
      )}
      <RecentPosts />
      <BackLink label="locations" path="/locations"/>
    </>
  );
};

export default LocationTemplate;

export const query = graphql`
  query location($id: String!) {
    page: wpLocation(id: { eq: $id }) {
      id
      nodeType
      title
      uri
      seo {
          title
          metaDesc
          focuskw
          metaKeywords
          metaRobotsNoindex
          metaRobotsNofollow
          opengraphTitle
          opengraphDescription
          opengraphImage {
              altText
              sourceUrl
              srcSet
          }
          twitterTitle
          twitterDescription
          twitterImage {
              altText
              sourceUrl
              srcSet
          }
          canonical
          cornerstone
          schema {
              articleType
              pageType
              raw
          }
      }
      metadata {
        metaDescription
        metaImage {
          ...WpImage
        }
      }
      sectionPromotions {
        promotions {
          promotion {
            ...Promotion
          }
        }
      }
      ...LocationDetails
    }
  }
`;
